import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  ChartingLibraryWidgetOptions,
  CustomFormatter,
  EntityId,
  IChartingLibraryWidget,
  LanguageCode,
  ResolutionString,
  ThemeName,
  Timezone,
} from "../../public/charting_library/charting_library";
import {
  useChartReady,
  useCurrentSymbol,
  useLocale,
  useTheme,
} from "../hooks/global.hooks";
import { DataFeedHelper } from "../services/_dataFeed.service";
import { _timeService } from "../services/_time.service";
import { getOverrides } from "../helpers/tradingViewHelper";
import "./chart.scss";
import { _chartReadyService } from "../services/_chartReady.service";
import IntervalSwitcher from "./intervalSwitcher/intervalSwitcher";
import IndicatorDropdown from "./IndicatorDropdown/IndicatorDropdown";
import { _rpcService } from "../services/_rpc.service";

const Chart = () => {
  const tradingViewContainerRef = useRef<HTMLDivElement>(null);
  const [widgetRef, setWidgetRef] = useState<
    IChartingLibraryWidget | undefined
  >(undefined);
  const { currentSymbol, symbolReady, symbol } = useCurrentSymbol();
  const widgetInitialized = useRef(false);
  const dataFeedRef = useRef<DataFeedHelper | null>(null);
  const { theme } = useTheme();
  const themeRef = useRef(theme);
  const { locale } = useLocale();
  const localeRef = useRef<string | null>(null);
  const pendingSavedStateRef = useRef<object | null>(null);

  const { chartIsReady } = useChartReady();
  const [activeChartType, setActiveChartType] = useState<number | null>(null);
  const [studies, setStudies] = useState<string[]>([]);
  const [externalIntervalControl, setExternalIntervalControl] = useState<
    boolean | null
  >(null);
  const [externalIndicatorControl, setExternalIndicatorControl] = useState<
    boolean | null
  >(null);
  const [defaultInterval, setDefaultInterval] = useState<string | null>(null);

  const initializeWidget = useCallback(() => {
    if (symbol === null) return;
    if (currentSymbol === null) return;
    if (symbolReady === false) return;
    if (externalIntervalControl && !defaultInterval) return;

    const startTime = performance.now(); // Capture the start time
    if (tradingViewContainerRef.current === null) {
      return;
    }

    const nextLocale = (locale || "en") as LanguageCode;
    localeRef.current = nextLocale;
    const savedState = pendingSavedStateRef.current;
    pendingSavedStateRef.current = null;

    _chartReadyService.setChartisReady(false);
    dataFeedRef.current = new DataFeedHelper();

    if (!dataFeedRef.current) return;
    const options: ChartingLibraryWidgetOptions = {
      container: tradingViewContainerRef.current?.id as string,
      autosize: true,
      load_last_chart: true,
      symbol: symbol,
      interval: (defaultInterval || "1") as ResolutionString,
      timezone: _timeService.getCurrentTimeZone() as Timezone,
      theme: theme as ThemeName,
      locale: nextLocale,
      custom_css_url: "/TrendingViewChart.css",
      datafeed: dataFeedRef.current,
      loading_screen:
        theme === "dark"
          ? { backgroundColor: "#0a0a0a", foregroundColor: "#03E4BA" }
          : { backgroundColor: "#fff", foregroundColor: "#0a6f6f" },
      disabled_features: [
        "header_symbol_search",
        "symbol_search_hot_key",
        "timeframes_toolbar",
        "header_compare",
        "header_widget",
        "left_toolbar",
        "legend_widget",
        "timezone_menu",
        "save_chart_properties_to_local_storage",
        "header_settings",
        "property_pages",
        "create_volume_indicator_by_default",
      ],
      library_path: "/charting_library/",
      custom_font_family: "'Montserrat', monospace",
      overrides: getOverrides(theme as ThemeName),
      custom_formatters: {
        timeFormatter: {
          format: dataFeedRef.current.formatTime.bind(dataFeedRef.current),
          formatLocal: dataFeedRef.current.formatTime.bind(dataFeedRef.current),
          parse: (value: string): string => "",
        } as CustomFormatter,
        dateFormatter: {
          format: dataFeedRef.current.formatDate.bind(dataFeedRef.current),
          formatLocal: dataFeedRef.current.formatDate.bind(dataFeedRef.current),
          parse: (value: string): string => "",
        } as CustomFormatter,
      },
    };
    const newWidget = new (window as any).TradingView.widget(
      options
    ) as IChartingLibraryWidget;
    setWidgetRef(newWidget);

    // Add reference to widget in dataFeed
    dataFeedRef.current.setWidget(newWidget);
    dataFeedRef.current?.setTheme(theme as ThemeName);

    newWidget.onChartReady(() => {
      if (savedState) {
        newWidget.load(savedState);
      }
      const currentChartType = newWidget.chart().chartType();
      setActiveChartType(currentChartType);

      newWidget
        .chart()
        .onChartTypeChanged()
        .subscribe(null, (chartType) => {
          setActiveChartType(chartType);
          _rpcService.notify("chartType.changed", { chart_type: chartType });
        });

      _chartReadyService.setChartisReady(true);

      const studies = newWidget.getStudiesList();
      setStudies(studies);

      const currentInterval = newWidget.chart().resolution();
      _rpcService.notify("interval.changed", { interval: currentInterval });

      const endTime = performance.now(); // Capture the end time
      console.log(`Time taken: ${endTime - startTime} milliseconds`); // Log the time difference
    });
  }, [
    currentSymbol,
    symbol,
    symbolReady,
    theme,
    locale,
    externalIntervalControl,
    defaultInterval,
  ]);

  const saveWidgetState = useCallback((): Promise<object | null> => {
    if (!widgetRef) {
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      try {
        widgetRef.save((state) => resolve(state));
      } catch (error) {
        resolve(null);
      }
    });
  }, [widgetRef]);

  useEffect(() => {
    if (currentSymbol && symbolReady && symbol) {
      if (!widgetRef && !widgetInitialized.current) {
        initializeWidget();
        widgetInitialized.current = true;
        return;
      }
    }
  }, [currentSymbol, symbolReady, symbol, widgetRef, initializeWidget]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    const applyTheme = (theme_: ThemeName): void => {
      widgetRef?.changeTheme(theme_).then(() => {
        widgetRef?.applyOverrides(getOverrides(theme_));
        dataFeedRef.current?.setTheme(theme_);
      });
    };

    // Listen for changes in the 'theme' prop
    if (widgetRef) {
      if (chartIsReady && theme !== themeRef.current) {
        themeRef.current = theme;
        dataFeedRef.current?.setTheme(theme as ThemeName);
        applyTheme(theme as ThemeName);
      }
      return;
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [theme, widgetRef, chartIsReady]);

  useEffect(() => {
    const nextLocale = locale || "en";
    if (!widgetRef) {
      localeRef.current = nextLocale;
      return;
    }
    if (localeRef.current === nextLocale) {
      return;
    }
    if (!chartIsReady) {
      return;
    }

    let cancelled = false;
    const reinitialize = async () => {
      const savedState = await saveWidgetState();
      if (cancelled) {
        return;
      }
      pendingSavedStateRef.current = savedState;
      widgetRef.remove();
      setWidgetRef(undefined);
      widgetInitialized.current = false;
      _chartReadyService.setChartisReady(false);
      localeRef.current = nextLocale;
    };

    reinitialize();

    return () => {
      cancelled = true;
    };
  }, [locale, widgetRef, chartIsReady, saveWidgetState]);

  useEffect(() => {
    const sub = _rpcService.subExternalIntervalControl().subscribe((value) => {
      setExternalIntervalControl(value);
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const sub = _rpcService
      .subExternalIndicatorControl()
      .subscribe((value) => {
        setExternalIndicatorControl(value);
      });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const sub = _rpcService.subDefaultInterval().subscribe((value) => {
      setDefaultInterval(value);
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!widgetRef) {
      _rpcService.registerIntervalHandler(null);
      _rpcService.registerChartTypeHandler(null);
      _rpcService.registerToggleChartTypeHandler(null);
      _rpcService.registerIndicatorHandlers(null);
      _rpcService.registerDrawingHandlers(null);
      return;
    }

    _rpcService.registerIntervalHandler((interval) => {
      const currentInterval = widgetRef.chart().resolution();
      if (currentInterval === interval) {
        _rpcService.notify("interval.changed", { interval });
        return;
      }
      widgetRef.chart().setResolution(interval as ResolutionString);
    });

    _rpcService.registerChartTypeHandler((chartType) => {
      widgetRef.chart().setChartType(chartType);
    });

    _rpcService.registerToggleChartTypeHandler(() => {
      const currentChartType = widgetRef.chart().chartType();
      const newChartType = currentChartType === 1 ? 3 : 1;
      widgetRef.chart().setChartType(newChartType);
    });

    _rpcService.registerIndicatorHandlers({
      getAvailable: () => widgetRef.getStudiesList(),
      listActive: () =>
        widgetRef
          .chart()
          .getAllStudies()
          .map((study) => ({ id: study.id, name: study.name })),
      add: async (name) => {
        const id = await widgetRef.chart().createStudy(name, false, false);
        if (!id) {
          throw new Error("Failed to create indicator");
        }
        return id;
      },
      remove: (id) => {
        const studies = widgetRef.chart().getAllStudies();
        const match = studies.find(
          (study) => String(study.id) === String(id)
        );
        if (!match) {
          return false;
        }
        widgetRef.chart().removeEntity(match.id);
        return true;
      },
    });

    _rpcService.registerDrawingHandlers({
      listActive: () =>
        widgetRef
          .chart()
          .getAllShapes()
          .map((shape) => ({ id: shape.id, name: shape.name })),
      add: (params) => {
        if (params.kind === "single") {
          return widgetRef.chart().createShape(params.point, params.options);
        }
        if (params.kind === "multi") {
          return widgetRef
            .chart()
            .createMultipointShape(params.points, params.options);
        }
        if (params.kind === "anchored") {
          return widgetRef
            .chart()
            .createAnchoredShape(params.position, params.options);
        }
        return null;
      },
      remove: (id) => {
        const shapes = widgetRef.chart().getAllShapes();
        const match = shapes.find(
          (shape) => String(shape.id) === String(id)
        );
        if (!match) {
          return false;
        }
        widgetRef.chart().removeEntity(match.id);
        return true;
      },
      selectTool: (params) => {
        widgetRef.selectLineTool(params.tool, params.options);
      },
    });

    return () => {
      _rpcService.registerIntervalHandler(null);
      _rpcService.registerChartTypeHandler(null);
      _rpcService.registerToggleChartTypeHandler(null);
      _rpcService.registerIndicatorHandlers(null);
      _rpcService.registerDrawingHandlers(null);
    };
  }, [widgetRef]);

  const handleIntervalChange = useCallback(
    (interval: string) => {
      if (widgetRef) {
        widgetRef.chart().setResolution(interval as ResolutionString);
      }
    },
    [widgetRef]
  );

  const handleToggleChartType = useCallback(() => {
    if (widgetRef) {
      const currentChartType = widgetRef.chart().chartType();
      const newChartType = currentChartType === 1 ? 3 : 1;
      widgetRef.chart().setChartType(newChartType);
    }
  }, [widgetRef]);

  const activateStudy = (studyName: string) => {
    if (widgetRef) {
      console.log("Activating study: ", studyName);
      widgetRef.chart().createStudy(studyName, false, false);
    }
  };

  const deactivateStudy = (studyName: string) => {
    if (widgetRef) {
      console.log("Deactivating study: ", studyName);
      const study = widgetRef
        .chart()
        .getAllStudies()
        .find((s) => s.name === studyName);
      if (study) {
        widgetRef.chart().removeEntity(study.id);
      }
    }
  };

  return (
    <>
      {activeChartType &&
      (!_rpcService.isRpcEnabled() || externalIndicatorControl === false) ? (
        <IndicatorDropdown
          studies={studies}
          activateStudy={activateStudy}
          deactivateStudy={deactivateStudy}
        />
      ) : null}
      <div
        id={"tradingview-widget-container"}
        ref={tradingViewContainerRef}
        className={`${!chartIsReady ? "notLoaded" : ""}`}
      ></div>

      {!chartIsReady && (
        <div className="spinner">
          <span className="spinner-inner-1"></span>
          <span className="spinner-inner-2"></span>
          <span className="spinner-inner-3"></span>
        </div>
      )}
      {activeChartType &&
      (!_rpcService.isRpcEnabled() || externalIntervalControl === false) ? (
        <IntervalSwitcher
          activeChartType={activeChartType}
          onIntervalChange={handleIntervalChange}
          toggleChartType={handleToggleChartType}
        />
      ) : (
        <div style={{ height: 41, backgroundColor: "transparent" }}></div>
      )}
    </>
  );
};

export default memo(Chart);
